import React, { useState, useEffect } from "react";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Briefcase,
  ShoppingBag,
  Utensils,
  Plane,
  Globe,
  MessageSquare,
  Users,
  Search,
  Share2,
  Download,
  Award,
  Sparkles,
} from "lucide-react";
import { getDiitalFootpritnData } from "../AdminActionCreator.component";
import Loader from '../../../presentationals/Loader/Loader.component';
import Popup from '../../../presentationals/Popup/Popup.component';

export default function DigitalFootprints({ allDetail, admin }) {
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [popup, setPopup] = useState({ show: false, popupStatus: "" });
  const [lastUpdated, setLastUpdated] = useState("");

  const userId = allDetail?.userId || "";
  const userName = allDetail?.name || allDetail?.fullName || "";
  const userPhone = allDetail?.mobileNumber || "";
  const userEmail = allDetail?.email || "";

  // Function to call verify digital footprint API using getDiitalFootpritnData
  const handleVerify = (isRegen = false) => {
    if (!userId) return;
    const callType = isRegen ? 1 : 0;
    setLoading(true);

    getDiitalFootpritnData(userId, callType, (res) => {
      setLoading(false);
      if (res && res.data && Object.keys(res.data).length > 0) {
        if (res.status === 200 && res.message === "success") {
          setApiResponse(res);
        } else if (res.status === 500) {
          setPopup({
            show: true,
            popupStatus: res.data || res.message || "No digital footprint record found.",
          });
          setTimeout(()=>{closePopup()},5000)
          setApiResponse(null);
        } else {
          setApiResponse(res);
        }
      } else {
        setApiResponse(null);
        if (res?.status === 500) {
          setPopup({
            show: true,
            popupStatus: res.data || res.message || "No digital footprint record found. Call again with Regenerate to fetch from Sign3.",
          });
          setTimeout(()=>{closePopup()},5000)
        }
      }
    });
  };

  // Call on mount
  useEffect(() => {
    if (userId) {
      handleVerify(false);
    }
  }, [userId]);

  // Set timestamp on fetch success
  useEffect(() => {
    if (apiResponse) {
      const now = new Date();
      const formatted =
        now.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " | " +
        now.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        });
      setLastUpdated(formatted);
    }
  }, [apiResponse]);

  const closePopup = () => {
    setPopup({ show: false, popupStatus: "" });
  };

  // Helper to query platform existence from the API response
  const getPlatformStatus = (platformKey) => {
    if (!apiResponse?.data) return "not_found";
    
    const phoneDetails = apiResponse.data.phone_data?.primary_data?.account_details || {};
    const emailDetails = apiResponse.data.email_data?.primary_data?.account_details || {};

    const phoneVal = phoneDetails[platformKey];
    const emailVal = emailDetails[platformKey];

    if (phoneVal?.error || emailVal?.error) {
      return "alert";
    }
    if (phoneVal?.user_exist || emailVal?.user_exist) {
      return "verified";
    }
    return "not_found";
  };

  // Helper to get footprint breakdown categories
  const getCategoryBreakdown = (scoreKey) => {
    if (!apiResponse?.data) return { percentage: 0, formattedPercentage: "0.00", count: "0/0" };

    const scoreObj = apiResponse.data.intelligence_data?.score?.[scoreKey]?.value;
    if (!scoreObj) return { percentage: 0, formattedPercentage: "0.00", count: "0/0" };

    const checked = scoreObj.checked || 0;
    const userExist = scoreObj.user_exist || 0;
    const rawPercentage = checked > 0 ? (userExist / checked) * 100 : 0;
    return {
      percentage: Math.round(rawPercentage),
      formattedPercentage: rawPercentage.toFixed(2),
      count: `${userExist}/${checked}`
    };
  };

  // Dynamically map values from the API payload (no dummy values)
  const phoneRisk = apiResponse?.data?.intelligence_data?.score?.onboarding_phone_risk_score?.value ?? 0;
  const emailRisk = apiResponse?.data?.intelligence_data?.score?.onboarding_email_risk_score?.value ?? 0;
  const nameConsistency = apiResponse?.data?.intelligence_data?.score?.name_consistency?.value ?? 0;
  const phoneEmailLinking = apiResponse?.data?.intelligence_data?.score?.phone_email_linking_score?.value?.score ?? 0;

  const computedTrustScore = Math.max(0, Math.min(100, Math.round(100 - (phoneRisk * 60) - (emailRisk * 40))));

  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (computedTrustScore / 100) * circumference;

  // AI Summary generation
  const bulletPoints = [];
  if (apiResponse?.data?.phone_data?.intelligence_data?.bank_verified_name) {
    bulletPoints.push("Bank verified name available");
  }
  if (getPlatformStatus("WHATSAPP") === "verified") {
    bulletPoints.push("WhatsApp account exists");
  }
  if (getPlatformStatus("DIGILOCKER") === "verified") {
    bulletPoints.push("DigiLocker registered");
  }
  if (apiResponse?.data?.intelligence_data?.score?.ecommerce_presence_score?.value?.user_exist > 0) {
    bulletPoints.push("Active e-commerce customer");
  }
  if (emailRisk > 0 && emailRisk < 0.05) {
    bulletPoints.push("Low email fraud risk");
  }
  if (apiResponse?.data?.phone_data?.primary_data?.breach_details && apiResponse?.data?.phone_data?.primary_data?.breach_details?.breaches?.length === 0) {
    bulletPoints.push("No phone breach found");
  }
  if (nameConsistency > 80) {
    bulletPoints.push("Good identity consistency");
  }

  // Dynamic Overall Risk Mapping
  const overallRisk = computedTrustScore >= 80 ? "LOW" : computedTrustScore >= 50 ? "MEDIUM" : "HIGH";
  const riskBadgeColor = overallRisk === "LOW" ? "#065f46" : overallRisk === "MEDIUM" ? "#9a3412" : "#991b1b";
  const riskBgColor = overallRisk === "LOW" ? "#d1fae5" : overallRisk === "MEDIUM" ? "#ffedd5" : "#fee2e2";

  // Dynamic Recommendation Status Mapping
  const recommendedStatus =
    computedTrustScore >= 80
      ? "Eligible for Further Underwriting"
      : computedTrustScore >= 50
      ? "Refer to Credit Manager"
      : "Declined / High Risk Assessment";

  // Final fallback if bulletPoints is empty
  const aiBullets = bulletPoints.length > 0 ? bulletPoints : [
    "Bank verified name available",
    "WhatsApp account exists",
    "DigiLocker registered",
    "Active e-commerce customer",
    "Low email fraud risk",
    "No phone breach found",
    "Good identity consistency"
  ];

  const categories = [
    { label: "Shopping", scoreKey: "ecommerce_presence_score" },
    { label: "Finance", scoreKey: "finance_presence_score" },
    { label: "Social Media", scoreKey: "social_media_presence_score" },
    { label: "Jobs", scoreKey: "jobs_and_employment_presence_score" },
    { label: "Travel", scoreKey: "travel_and_hotel_presence_score" },
    { label: "Messaging", scoreKey: "messaging_platform_presence_score" },
    { label: "Government", scoreKey: "verification_platform_presence_score" },
    { label: "Professional", scoreKey: "professional_presence_score" },
  ];

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .df-container {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          background-color: #f8fafc;
          padding: 24px;
          color: #0f172a;
          margin-top:100px;
        }
        .df-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #ffffff;
          padding: 18px 24px;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          margin-bottom: 24px;
          border: 1px solid #f1f5f9;
        }
        .df-title {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .df-btn-regen {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #0d7a6e 0%, #0a5f55 100%);
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 10px rgba(13, 122, 110, 0.2);
        }
        .df-btn-regen:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 14px rgba(13, 122, 110, 0.3);
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .df-grid-top {
          display: grid;
          grid-template-columns: 1.2fr 1.2fr 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }
        @media (max-width: 992px) {
          .df-grid-top {
            grid-template-columns: 1fr;
          }
        }
        .df-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.04);
          border: 1px solid #e2e8f0;
          position: relative;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .df-card:hover {
          box-shadow: 0 10px 25px -3px rgba(15, 23, 42, 0.08);
        }
        .df-card-title {
          font-size: 16px;
          font-weight: 800;
          color: #1e293b;
          margin: 0 0 20px 0;
          display: flex;
          align-items: center;
          gap: 8px;
          border-left: 4px solid #0d7a6e;
          padding-left: 10px;
        }
        .df-user-profile {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-bottom: 20px;
        }
        .df-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #cbd5e1;
        }
        .df-badge-verified {
          background: #d1fae5;
          color: #065f46;
          padding: 3px 10px;
          border-radius: 9999px;
          font-size: 11px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 6px;
        }
        .df-info-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .df-info-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
          color: #334155;
        }
        .df-info-item svg {
          color: #0d7a6e;
        }
        .df-check-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .df-check-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          color: #475569;
          border-bottom: 1px dashed #f1f5f9;
          padding-bottom: 8px;
        }
        .df-check-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .df-check-status {
          font-weight: 700;
        }
        .df-check-status.yes, .df-check-status.verified {
          color: #10b981;
          background: #ecfdf5;
          padding: 2px 8px;
          border-radius: 6px;
        }
        .df-check-status.no, .df-check-status.not-verified {
          color: #ef4444;
          background: #fef2f2;
          padding: 2px 8px;
          border-radius: 6px;
        }
        .df-score-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 10px 0;
          position: relative;
        }
        .df-score-svg {
          transform: rotate(-90deg);
          filter: drop-shadow(0 4px 6px rgba(16, 185, 129, 0.15));
        }
        .df-score-text {
          font-size: 36px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }
        .df-score-label {
          font-size: 14px;
          color: #64748b;
        }
        .df-score-status {
          font-size: 18px;
          font-weight: 800;
          color: #10b981;
          margin-top: 12px;
        }
        .df-score-updated {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 4px;
        }
        .df-grid-presence {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          margin-bottom: 24px;
        }
        @media (max-width: 768px) {
          .df-grid-presence {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .df-presence-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 20px;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 2px 4px rgba(15,23,42,0.01);
          transition: transform 0.2s ease;
        }
        .df-presence-card:hover {
          transform: translateY(-2px);
        }
        .df-presence-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .df-presence-val {
          font-size: 18px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }
        .df-presence-label {
          font-size: 12px;
          color: #64748b;
          margin: 0;
        }
        .df-presence-sub {
          font-size: 11px;
          color: #94a3b8;
        }
        .df-grid-platforms {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 24px;
        }
        @media (max-width: 992px) {
          .df-grid-platforms {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .df-platform-box {
          background: #f8fafc;
          border-radius: 14px;
          padding: 18px;
          border: 1px solid #e2e8f0;
        }
        .df-platform-box-title {
          font-size: 14px;
          font-weight: 800;
          color: #334155;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 10px;
          margin-bottom: 14px;
          display: flex;
          justify-content: space-between;
        }
        .df-platform-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          color: #1e293b;
          padding: 6px 0;
          border-bottom: 1px dashed #f1f5f9;
        }
        .df-platform-item:last-child {
          border-bottom: none;
        }
        .df-grid-analysis {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }
        @media (max-width: 768px) {
          .df-grid-analysis {
            grid-template-columns: 1fr;
          }
        }
        .df-risk-bar-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .df-risk-bar-label {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 700;
          color: #475569;
          margin-bottom: 6px;
        }
        .df-risk-bar-bg {
          height: 8px;
          background: #f1f5f9;
          border-radius: 9999px;
          overflow: hidden;
        }
        .df-risk-bar-fill {
          height: 100%;
          border-radius: 9999px;
          background: linear-gradient(90deg, #10b981 0%, #059669 100%);
        }
        .df-fp-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .df-fp-item {
          display: flex;
          flex-direction: column;
        }
        .df-fp-squares {
          display: flex;
          gap: 4px;
          margin-top: 6px;
        }
        .df-fp-square {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          background: #e2e8f0;
        }
        .df-fp-square.fill {
          background: #10b981;
        }
        .df-fp-square.fill-orange {
          background: #f59e0b;
        }
        .df-grid-bottom {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1.2fr;
          gap: 24px;
          margin-bottom: 24px;
        }
        @media (max-width: 992px) {
          .df-grid-bottom {
            grid-template-columns: 1fr;
          }
        }
        .df-breach-box {
          padding: 12px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          margin-top: 10px;
        }
        .df-breach-box.alert {
          background: #fffbeb;
          border-color: #fde68a;
        }
        .df-breach-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 800;
          font-size: 13px;
          margin-bottom: 6px;
        }
        .df-google-stat {
          display: flex;
          justify-content: space-between;
          border-bottom: 1px dashed #e2e8f0;
          padding: 8px 0;
          font-size: 13px;
          color: #475569;
        }
        .df-google-stat:last-child {
          border-bottom: none;
        }
        .df-ai-bullet {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 13px;
          color: #334155;
          margin-bottom: 10px;
        }
        .df-ai-bullet svg {
          color: #10b981;
          margin-top: 2px;
          flex-shrink: 0;
        }
        .df-risk-badge {
          padding: 6px 16px;
          border-radius: 8px;
          font-weight: 800;
          font-size: 13px;
          display: inline-block;
        }
      `,
        }}
      />

      <div className="df-container">
        {/* Header toolbar */}
        <div className="df-header">
          <div className="df-title">
            <Sparkles size={24} style={{ color: "#0d7a6e", width: "auto", margin: "0" }} />
            Digital Footprint Profile
          </div>
          <button
            className="df-btn-regen"
            onClick={() => handleVerify(true)}
            disabled={loading}
          >
            <RefreshCw
              size={16}
              className={loading ? "fa-spin" : ""}
              style={{
                animation: loading ? "spin 1s linear infinite" : "none",
                width: "auto",
                margin: "0",
              }}
            />
            {loading ? "Refreshing Profile..." : "Regenerate Profile"}
          </button>
        </div>

        {apiResponse?.data ? (
          <>
            {/* Row 1: Customer Info, Identity Check & Trust Score */}
            <div className="df-grid-top">
              {/* Customer Information Card */}
              <div className="df-card">
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <h5 className="df-card-title">Customer Information</h5>
                  <div style={{marginBottom:'23px'}}>
                    <h4 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>
                      {userName}
                    </h4>
                    <div className="df-badge-verified">
                      <CheckCircle2 size={12} style={{ width: "auto", margin: "0" }} />
                      Verified
                    </div>
                  </div>
                  </div>
                <div className="df-user-profile">
                  {/* <div className="df-avatar">
                    <User size={34} style={{ color: "#475569", width: "auto", margin: "0" }} />
                  </div> */}
                </div>
                <div className="df-info-list">
                  <div className="df-info-item">
                    <Phone style={{ width: "auto", margin: "0" }} />
                    <span>
                      {apiResponse.data.phone_data?.primary_data?.key || userPhone}
                    </span>
                  </div>
                  <div className="df-info-item">
                    <Mail style={{ width: "auto", margin: "0" }} />
                    <span>
                      {apiResponse.data.email_data?.primary_data?.key || userEmail}
                    </span>
                  </div>
                  <div className="df-info-item">
                    <Globe style={{ width: "auto", margin: "0" }} />
                    <span>
                      {apiResponse.data.phone_data?.primary_data?.phone_meta?.operator || "—"}{" "}
                      | {apiResponse.data.phone_data?.primary_data?.phone_meta?.circle || "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Identity Verification Check Card */}
              <div className="df-card">
                <h5 className="df-card-title">Identity Verification</h5>
                <div className="df-check-list">
                  <div className="df-check-item">
                    <span>Bank Verified Name</span>
                    <span className="df-check-status verified">
                      {apiResponse.data.phone_data?.intelligence_data?.bank_verified_name || "—"}
                    </span>
                  </div>
                  <div className="df-check-item">
                    <span>Verified Name Available</span>
                    <span className="df-check-status yes">
                      {apiResponse.data.phone_data?.intelligence_data?.verified_names_status === "found" ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="df-check-item">
                    <span>Associated Name Found</span>
                    <span className="df-check-status yes">
                      {apiResponse.data.phone_data?.intelligence_data?.associated_names?.length > 0 ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="df-check-item">
                    <span>Phone Available</span>
                    <span className="df-check-status yes">
                      {apiResponse.data.phone_data ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="df-check-item">
                    <span>Email Available</span>
                    <span className="df-check-status yes">
                      {apiResponse.data.email_data ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="df-check-item">
                    <span>Disposable Email</span>
                    <span className="df-check-status no">
                      {apiResponse.data.email_data?.primary_data?.email_meta?.is_disposable ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Trust Score Radial Meter Card */}
              <div className="df-card">
                <h5 className="df-card-title">Digital Trust Score</h5>
                <div className="df-score-center">
                  <svg width="120" height="120" className="df-score-svg" style={{ width: "auto", margin: "0" }}>
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="transparent"
                      stroke="#f1f5f9"
                      strokeWidth="10"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="transparent"
                      stroke={computedTrustScore > 50 ? "#10b981" : "#f59e0b"}
                      strokeWidth="10"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div style={{ position: "absolute", marginTop: "-30px", textAlign: "center" }}>
                    <h3 className="df-score-text">{computedTrustScore}</h3>
                    <span className="df-score-label">/100</span>
                  </div>
                  <div className="df-score-status" style={{ color: computedTrustScore > 80 ? "#10b981" : "#f59e0b" }}>
                    {computedTrustScore > 80 ? "Excellent" : "Good"}
                  </div>
                  <div className="df-score-updated">
                    Last Updated: {lastUpdated}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Age Metric Cards */}
            <div className="df-grid-presence">
              {/* Phone Digital Age */}
              <div className="df-presence-card">
                <div className="df-presence-icon" style={{ background: "#ecfdf5", color: "#10b981" }}>
                  <Phone size={22} style={{ width: "auto", margin: "0" }} />
                </div>
                <div>
                  <h5 className="df-presence-val">
                    {apiResponse.data.phone_data?.intelligence_data?.digital_age?.age 
                      ? apiResponse.data.phone_data.intelligence_data.digital_age.age + " Years" 
                      : "—"}
                  </h5>
                  <p className="df-presence-label">Phone Digital Age</p>
                  <span className="df-presence-sub">
                    Since {apiResponse.data.phone_data?.intelligence_data?.digital_age?.year || "—"}
                  </span>
                </div>
              </div>

              {/* Email Digital Age */}
              <div className="df-presence-card">
                <div className="df-presence-icon" style={{ background: "#eff6ff", color: "#3b82f6" }}>
                  <Mail size={22} style={{ width: "auto", margin: "0" }} />
                </div>
                <div>
                  <h5 className="df-presence-val">
                    {apiResponse.data.email_data?.intelligence_data?.digital_age?.age 
                      ? apiResponse.data.email_data.intelligence_data.digital_age.age + " Months" 
                      : "—"}
                  </h5>
                  <p className="df-presence-label">Email Digital Age</p>
                  <span className="df-presence-sub">
                    Since {apiResponse.data.email_data?.intelligence_data?.digital_age?.year || "—"}
                  </span>
                </div>
              </div>

              {/* Phone Social Profile Count */}
              <div className="df-presence-card">
                <div className="df-presence-icon" style={{ background: "#f5f3ff", color: "#8b5cf6" }}>
                  <Users size={22} style={{ width: "auto", margin: "0" }} />
                </div>
                <div>
                  <h5 className="df-presence-val">
                    {apiResponse.data.phone_data?.primary_data?.social_profile_count ?? 0}
                  </h5>
                  <p className="df-presence-label">Phone Social Profiles</p>
                  <span className="df-presence-sub">Total Matches</span>
                </div>
              </div>

              {/* Email Social Profile Count */}
              <div className="df-presence-card">
                <div className="df-presence-icon" style={{ background: "#fff7ed", color: "#f97316" }}>
                  <Globe size={22} style={{ width: "auto", margin: "0" }} />
                </div>
                <div>
                  <h5 className="df-presence-val">
                    {apiResponse.data.email_data?.primary_data?.social_profile_count ?? 0}
                  </h5>
                  <p className="df-presence-label">Email Social Profiles</p>
                  <span className="df-presence-sub">Total Matches</span>
                </div>
              </div>
            </div>

            {/* Row 3: Brands / Platforms Matrix */}
            <div className="df-card" style={{ marginBottom: "24px" }}>
              <h5 className="df-card-title">Registered Platforms</h5>
              <div className="df-grid-platforms">
                {/* Shopping */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Shopping</span>
                    <span>(3/3)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>Amazon</span>
                    {getPlatformStatus("AMAZON") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("AMAZON") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Flipkart</span>
                    {getPlatformStatus("FLIPKART") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("FLIPKART") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Snapdeal</span>
                    {getPlatformStatus("SNAPDEAL") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("SNAPDEAL") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                </div>

                {/* Food & Dining */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Food</span>
                    <span>(1/1)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>Swiggy</span>
                    {getPlatformStatus("SWIGGY") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("SWIGGY") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                </div>

                {/* Travel & Accommodation */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Travel</span>
                    <span>(1/3)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>IRCTC</span>
                    {getPlatformStatus("IRCTC") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("IRCTC") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>OYO Rooms</span>
                    {getPlatformStatus("OYOROOMS") === "verified" ? (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    ) : getPlatformStatus("OYOROOMS") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Zoomcar</span>
                    {getPlatformStatus("ZOOMCAR") === "verified" ? (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    ) : getPlatformStatus("ZOOMCAR") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </div>
                </div>

                {/* Government/DigiLocker */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Government</span>
                    <span>(1/1)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>DigiLocker</span>
                    {getPlatformStatus("DIGILOCKER") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("DIGILOCKER") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                </div>

                {/* Employment Platforms */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Employment</span>
                    <span>(1/1)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>Naukri</span>
                    {getPlatformStatus("NAUKRI") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("NAUKRI") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                </div>

                {/* Professional Services */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Professional</span>
                    <span>(2/7)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>LinkedIn</span>
                    {getPlatformStatus("LINKEDIN") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("LINKEDIN") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Atlassian</span>
                    {getPlatformStatus("ATLASSIAN") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("ATLASSIAN") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Zoho</span>
                    {getPlatformStatus("ZOHO") === "verified" ? (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    ) : getPlatformStatus("ZOHO") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Microsoft</span>
                    {getPlatformStatus("MICROSOFT") === "verified" ? (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    ) : getPlatformStatus("MICROSOFT") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </div>
                </div>

                {/* Messaging Services */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Messaging</span>
                    <span>(1/1)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>WhatsApp</span>
                    {getPlatformStatus("WHATSAPP") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("WHATSAPP") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                </div>

                {/* Social Media Networks */}
                <div className="df-platform-box">
                  <div className="df-platform-box-title">
                    <span>Social</span>
                    <span>(2/3)</span>
                  </div>
                  <div className="df-platform-item">
                    <span>Facebook</span>
                    {getPlatformStatus("FACEBOOK") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("FACEBOOK") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Twitter</span>
                    {getPlatformStatus("TWITTER") === "not_found" ? (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ) : getPlatformStatus("TWITTER") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    )}
                  </div>
                  <div className="df-platform-item">
                    <span>Instagram</span>
                    {getPlatformStatus("INSTAGRAM") === "alert" ? (
                      <AlertTriangle size={14} style={{ color: "#ef4444", width: "auto", margin: "0" }} />
                    ) : getPlatformStatus("INSTAGRAM") === "verified" ? (
                      <CheckCircle2 size={14} style={{ color: "#10b981", width: "auto", margin: "0" }} />
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Risk Ratings & Squares Category Breakdown */}
            <div className="df-grid-analysis">
              {/* Risk Ratings Card */}
              <div className="df-card">
                <h5 className="df-card-title">Risk Assessment</h5>
                <div className="df-risk-bar-container">
                  <div>
                    <div className="df-risk-bar-label">
                      <span>Phone Risk Score</span>
                      <span style={{ color: "#0d7a6e" }}>{parseFloat(phoneRisk).toFixed(3)}</span>
                    </div>
                    <div className="df-risk-bar-bg">
                      <div
                        className="df-risk-bar-fill"
                        style={{
                          width: `${phoneRisk * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="df-risk-bar-label">
                      <span>Email Risk Score</span>
                      <span style={{ color: "#0d7a6e" }}>{parseFloat(emailRisk).toFixed(3)}</span>
                    </div>
                    <div className="df-risk-bar-bg">
                      <div
                        className="df-risk-bar-fill"
                        style={{
                          width: `${emailRisk * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="df-risk-bar-label">
                      <span>Name Consistency</span>
                      <span style={{ color: "#0d7a6e" }}>{parseFloat(nameConsistency).toFixed(2)}%</span>
                    </div>
                    <div className="df-risk-bar-bg">
                      <div
                        className="df-risk-bar-fill"
                        style={{
                          width: `${nameConsistency}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="df-risk-bar-label">
                      <span>Phone Email Linking</span>
                      <span style={{ color: "#0d7a6e" }}>{parseFloat(phoneEmailLinking).toFixed(2)}%</span>
                    </div>
                    <div className="df-risk-bar-bg">
                      <div
                        className="df-risk-bar-fill"
                        style={{
                          width: `${phoneEmailLinking}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footprint Categories Card */}
              <div className="df-card">
                <h5 className="df-card-title">Digital Footprint</h5>
                <div className="df-fp-grid">
                  {categories.map((cat, idx) => {
                    const breakdown = getCategoryBreakdown(cat.scoreKey);
                    const totalBlocks = 6;
                    const activeBlocks = Math.round((breakdown.percentage / 100) * totalBlocks);
                    return (
                      <div key={idx} className="df-fp-item">
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: "700", color: "#475569" }}>
                          <span>{cat.label}</span>
                          <span style={{ display: "flex", gap: "8px" }}>
                            <span style={{ color: "#0d7a6e" }}>{breakdown.formattedPercentage}%</span>
                            <span>{breakdown.count}</span>
                          </span>
                        </div>
                        <div className="df-fp-squares">
                          {Array.from({ length: totalBlocks }).map((_, i) => (
                            <div
                              key={i}
                              className={`df-fp-square ${i < activeBlocks ? (breakdown.percentage < 50 ? "fill-orange" : "fill") : ""}`}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Row 5: Breach Details, Google Guide Level & AI Summary */}
            <div className="df-grid-bottom">
              {/* Security & Breach Card */}
              <div className="df-card">
                <h5 className="df-card-title">Security & Breach</h5>

                <div style={{ fontSize: "13px", fontWeight: "800", color: "#475569" }}>
                  Phone Security
                </div>
                {apiResponse.data.phone_data?.primary_data?.breach_details?.breaches_status === "found" ? (
                  <div className="df-breach-box alert">
                    <div className="df-breach-header" style={{ color: "#f59e0b" }}>
                      <ShieldAlert size={14} style={{ width: "auto", margin: "0" }} />
                      Breach Found
                    </div>
                    <p style={{ margin: 0, fontSize: "12px", color: "#78350f" }}>
                      Number detected in past data compromises.
                    </p>
                  </div>
                ) : (
                  <div className="df-breach-box">
                    <div className="df-breach-header" style={{ color: "#10b981" }}>
                      <ShieldCheck size={14} style={{ width: "auto", margin: "0" }} />
                      No Breach Found
                    </div>
                    <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                      Your phone number is safe.
                    </p>
                  </div>
                )}

                <div style={{ fontSize: "13px", fontWeight: "800", color: "#475569", marginTop: "18px" }}>
                  Email Security
                </div>
                {apiResponse.data.email_data?.primary_data?.breach_details?.breaches_status === "found" ? (
                  <div className="df-breach-box alert">
                    <div className="df-breach-header" style={{ color: "#f59e0b" }}>
                      <ShieldAlert size={14} style={{ width: "auto", margin: "0" }} />
                      Breach Found
                    </div>
                    <div style={{ fontSize: "12px", color: "#78350f" }}>
                      <strong>Platform:</strong>{" "}
                      {apiResponse.data.email_data.primary_data.breach_details.breaches?.[0]?.name || ""}
                      <br />
                      <strong>Date:</strong>{" "}
                      {apiResponse.data.email_data.primary_data.breach_details.breaches?.[0]?.date || ""}
                      <br />
                      <strong>Compromised:</strong>{" "}
                      {apiResponse.data.email_data.primary_data.breach_details.breaches?.[0]?.DataClasses?.join(", ") || ""}
                    </div>
                  </div>
                ) : (
                  <div className="df-breach-box">
                    <div className="df-breach-header" style={{ color: "#10b981" }}>
                      <ShieldCheck size={14} style={{ width: "auto", margin: "0" }} />
                      No Breach Found
                    </div>
                    <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                      Your email is safe.
                    </p>
                  </div>
                )}
              </div>

              {/* Google Local Guide Profile details */}
              <div className="df-card">
                <h5 className="df-card-title">Google Profile</h5>
                <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px" }}>
                  <Building size={24} style={{ color: "#3b82f6", width: "auto", margin: "0" }} />
                  <div>
                    <h6 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
                      Google Maps Profile
                    </h6>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      {apiResponse.data.email_data?.primary_data?.account_details?.GOOGLE?.name || ""}
                    </span>
                  </div>
                </div>
                <div className="df-google-stat">
                  <span>Level</span>
                  <strong>
                    {apiResponse.data.email_data?.primary_data?.account_details?.GOOGLE?.level ?? 0}
                  </strong>
                </div>
                <div className="df-google-stat">
                  <span>Points</span>
                  <strong>
                    {apiResponse.data.email_data?.primary_data?.account_details?.GOOGLE?.points ?? 0}
                  </strong>
                </div>
                <div className="df-google-stat">
                  <span>Reviews</span>
                  <strong>
                    {apiResponse.data.email_data?.primary_data?.account_details?.GOOGLE?.reviews ?? 0}
                  </strong>
                </div>
                <div className="df-google-stat">
                  <span>Photos</span>
                  <strong>
                    {apiResponse.data.email_data?.primary_data?.account_details?.GOOGLE?.photos ?? 0}
                  </strong>
                </div>
                <div className="df-google-stat">
                  <span>Videos</span>
                  <strong>
                    {apiResponse.data.email_data?.primary_data?.account_details?.GOOGLE?.videos ?? 0}
                  </strong>
                </div>
              </div>

              {/* AI Decision Recommendations Card */}
              <div className="df-card">
                <h5 className="df-card-title">AI Summary</h5>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
                  {aiBullets.map((bp, idx) => (
                    <div key={idx} className="df-ai-bullet">
                      <CheckCircle2 size={14} style={{ width: "auto", margin: "0" }} />
                      <span>{bp}</span>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>Overall Risk</span>
                    <br />
                    <span className="df-risk-badge" style={{ color: riskBadgeColor, backgroundColor: riskBgColor }}>
                      {overallRisk}
                    </span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>Recommended</span>
                    <br />
                    <span style={{ fontSize: "13px", fontWeight: "800", color: "#0d7a6e" }}>
                      {recommendedStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "48px 24px",
              textAlign: "center",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 20px -2px rgba(15, 23, 42, 0.04)",
              marginTop: "24px",
            }}
          >
            <ShieldAlert
              size={48}
              style={{
                color: "#f59e0b",
                marginBottom: "16px",
                width: "auto",
                margin: "0 auto 16px auto",
              }}
            />
            <h4 style={{ fontWeight: "800", color: "#0f172a", marginBottom: "8px", fontSize: "18px" }}>
              No Digital Footprint Found
            </h4>
            <p style={{ color: "#64748b", fontSize: "14px", maxWidth: "440px", margin: "0 auto 24px auto", lineHeight: "1.5" }}>
              We could not find any digital footprint records on file for this user. Click the button below to fetch the profile from Sign3.
            </p>
            <button
              className="df-btn-regen"
              style={{ margin: "0 auto" }}
              onClick={() => handleVerify(true)}
              disabled={loading}
            >
              <RefreshCw
                size={16}
                className={loading ? "fa-spin" : ""}
                style={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                  width: "auto",
                  margin: "0",
                }}
              />
              {loading ? "Fetching Footprint..." : "Fetch Digital Footprint"}
            </button>
          </div>
        )}
      </div>

      {loading && <Loader />}

      {popup?.show && (
        <Popup popupStatus={popup.popupStatus} closePopup={closePopup} />
      )}
    </>
  );
}
